import { Column, Row } from "@/components";
import { GoalMetricPanel } from "@/components/dashboard/dashboard-stats/main-stats/GoalMetricPanel";
import { TtfsGoalPanel } from "@/components/dashboard/dashboard-stats/main-stats/TtfsGoalPanel";
import { Skeleton } from "@/components/ui/skeleton";

import {
  DASHBOARD_STAT_CARD_HEIGHT_CLASS,
  screenTimeTier,
} from "@/lib/constants";
import { dashboardKpis } from "@/lib/dashboardKpis";
import { formatting } from "@/lib/formatting";
import { cn } from "@/lib/utils";
import {
  DashboardComparisonMetric,
  DashboardKpiOverview,
  DashboardSelectedUser,
  GoalPeriod,
  PerformanceTier,
} from "@/types";

// Time to first screen, submits, revenue.
const GOAL_PANEL_COUNT = 3;

export const DashboardTeamGoalsCard = ({
  goalsPeriod,
  isPending,
  onGoalsPeriodChange,
  onOpenScreenBreakdown,
  onSelectComparisonMetric,
  overview,
  selectedComparisonMetric = DashboardComparisonMetric.TIME_TO_SCREEN,
  selectedUserId,
}: {
  goalsPeriod: GoalPeriod;
  isPending: boolean;
  onGoalsPeriodChange: (period: GoalPeriod) => void;
  // Individual view only — the breakdown is per staffer.
  onOpenScreenBreakdown?: () => void;
  onSelectComparisonMetric?: (metric: DashboardComparisonMetric) => void;
  overview: DashboardKpiOverview | undefined;
  selectedComparisonMetric?: DashboardComparisonMetric;
  selectedUserId?: DashboardSelectedUser;
}) => {
  /* Helpers */
  const isIndividual = typeof selectedUserId === "number";
  const periodData = dashboardKpis.periodFor(overview, goalsPeriod);

  const medianHours = periodData
    ? periodData.totals.medianHoursToFirstScreen
    : null;
  // No screens yet reads as the worst grade rather than a false green.
  const ttfsTier =
    medianHours === null ? PerformanceTier.RED : screenTimeTier(medianHours);
  const submits = periodData ? periodData.totals.submits : 0;
  const revenue = periodData ? periodData.totals.revenue : 0;

  const goalFor = (metric: DashboardComparisonMetric) =>
    dashboardKpis.goalFor({
      metric,
      overview,
      perStaffer: isIndividual,
      period: goalsPeriod,
    });
  const submitsGoal = goalFor(DashboardComparisonMetric.SUBMITS);
  const revenueGoal = goalFor(DashboardComparisonMetric.REVENUE);

  const isSelected = (metric: DashboardComparisonMetric) =>
    selectedComparisonMetric === metric;

  /* Handlers */
  const handleSelectComparisonMetric = (metric: DashboardComparisonMetric) => {
    onSelectComparisonMetric?.(metric);
  };

  /* Render */
  const renderContent = () => {
    if (isPending) {
      return (
        <Row className="min-h-0 flex-1 items-stretch justify-around gap-3">
          {Array.from({ length: GOAL_PANEL_COUNT }, (_, index) => (
            <Skeleton key={index} className="min-w-0 flex-1 rounded-lever-md" />
          ))}
        </Row>
      );
    }

    const panelClassName = cn(
      "min-w-0 flex-1 items-center",
      onSelectComparisonMetric && "cursor-pointer",
    );

    return (
      <Row className="min-h-0 flex-1 items-stretch justify-around gap-3">
        <Column
          className={cn(
            "relative min-w-0 flex-1 self-stretch",
            onSelectComparisonMetric && "cursor-pointer",
          )}
          onClick={() => {
            handleSelectComparisonMetric(
              DashboardComparisonMetric.TIME_TO_SCREEN,
            );
          }}
        >
          <Column className="absolute inset-0 items-center justify-center">
            <TtfsGoalPanel
              goalsPeriod={goalsPeriod}
              isIndividual={isIndividual}
              isSelected={isSelected(DashboardComparisonMetric.TIME_TO_SCREEN)}
              medianHours={medianHours}
              onGoalsPeriodChange={onGoalsPeriodChange}
              onOpenScreenBreakdown={onOpenScreenBreakdown}
              tier={ttfsTier}
            />
          </Column>
        </Column>
        <Column
          className={panelClassName}
          onClick={() => {
            handleSelectComparisonMetric(DashboardComparisonMetric.SUBMITS);
          }}
        >
          <GoalMetricPanel
            barClassName="bg-lever-surface-action"
            borderClassName={
              isSelected(DashboardComparisonMetric.SUBMITS)
                ? "border-lever-border-action"
                : "border-lever-border-light"
            }
            goal={submitsGoal}
            goalLabel={submitsGoal.toLocaleString()}
            goalsPeriod={goalsPeriod}
            isSelected={isSelected(DashboardComparisonMetric.SUBMITS)}
            label="Submits"
            onGoalsPeriodChange={onGoalsPeriodChange}
            panelClassName={
              isSelected(DashboardComparisonMetric.SUBMITS)
                ? "bg-lever-surface-information"
                : "bg-lever-surface-default"
            }
            remainingLabel={Math.max(submitsGoal - submits, 0).toLocaleString()}
            textClassName={
              isSelected(DashboardComparisonMetric.SUBMITS)
                ? "text-lever-text-heading"
                : "text-lever-text-heading"
            }
            value={submits}
            valueLabel={submits.toLocaleString()}
          />
        </Column>
        <Column
          className={panelClassName}
          onClick={() => {
            handleSelectComparisonMetric(DashboardComparisonMetric.REVENUE);
          }}
        >
          <GoalMetricPanel
            barClassName="bg-lever-surface-accent-green"
            borderClassName={
              isSelected(DashboardComparisonMetric.REVENUE)
                ? "border-lever-border-success"
                : "border-lever-border-light"
            }
            goal={revenueGoal}
            goalLabel={formatting.compactUsd(revenueGoal)}
            goalsPeriod={goalsPeriod}
            isSelected={isSelected(DashboardComparisonMetric.REVENUE)}
            label="Revenue"
            onGoalsPeriodChange={onGoalsPeriodChange}
            panelClassName={
              isSelected(DashboardComparisonMetric.REVENUE)
                ? "bg-lever-surface-success"
                : "bg-lever-surface-default"
            }
            remainingLabel={formatting.currency(
              Math.max(revenueGoal - revenue, 0),
            )}
            textClassName={
              isSelected(DashboardComparisonMetric.REVENUE)
                ? "text-lever-text-heading"
                : "text-lever-text-heading"
            }
            value={revenue}
            valueLabel={formatting.compactUsd(revenue, { decimals: 1 })}
          />
        </Column>
      </Row>
    );
  };

  const render = () => (
    <Column
      className={cn(
        "min-h-0 w-full min-w-0 overflow-visible",
        DASHBOARD_STAT_CARD_HEIGHT_CLASS,
      )}
    >
      {renderContent()}
    </Column>
  );

  return render();
};
